import { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import Layout from '../Layout'

interface Account {
  id: number
  name: string
  slug: string
  price_input_mode?: number
}

interface Props {
  categories: Array<{ id: number; name: string }>
  account: Account
  errors?: string[]
  permissions?: { can_manage_products: boolean }
}

export default function ProductsNew({ categories, account, errors, permissions }: Props) {
  const [trackVariants, setTrackVariants] = useState(false)
  const withTax = account.price_input_mode === 1

  const [name, setName] = useState('')
  const [sku, setSku] = useState('')
  const [price, setPrice] = useState('')
  const [costPrice, setCostPrice] = useState('')
  const [stock, setStock] = useState('0')
  const [description, setDescription] = useState('')
  const [categoryId, setCategoryId] = useState('')

  const [taxRate, setTaxRate] = useState('')
  const [unitType, setUnitType] = useState('unit')
  const [trackInventory, setTrackInventory] = useState(true)
  const [requiresSerial, setRequiresSerial] = useState(false)
  const [maxWebInventory, setMaxWebInventory] = useState('')

  const [images, setImages] = useState<File[]>([])
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)

    const formData = new FormData()
    formData.append('product[name]', name)
    formData.append('product[sku]', sku)
    formData.append('product[price]', price)
    formData.append('product[cost_price]', costPrice)
    formData.append('product[stock_quantity]', trackVariants ? '0' : stock)
    formData.append('product[description]', description)
    if (categoryId) formData.append('product[category_id]', categoryId)
    formData.append('product[track_variants]', trackVariants ? 'true' : 'false')

    formData.append('product[tax_rate]', taxRate || '')
    formData.append('product[unit_type]', unitType)
    formData.append('product[track_inventory]', trackInventory ? 'true' : 'false')
    formData.append('product[requires_serial]', requiresSerial ? 'true' : 'false')
    if (maxWebInventory) formData.append('product[max_web_inventory]', maxWebInventory)

    images.forEach((img) => formData.append('product[images][]', img))

    router.post(`/${account.slug}/products`, formData, {
      onFinish: () => setSubmitting(false),
    })
  }

  return (
    <Layout account={account} permissions={permissions}>
      <div className="max-w-3xl mx-auto">
        <h1 className="text-2xl font-bold text-[#202020] mb-6">Nuevo Producto</h1>

        {errors && errors.length > 0 && (
          <div className="bg-red-50 text-red-600 p-3 rounded mb-4">
            {errors.map((error, i) => <p key={i}>{error}</p>)}
          </div>
        )}

        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <p className="text-sm font-medium text-gray-700 mb-3">Tipo de producto:</p>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="product_type" checked={!trackVariants} onChange={() => setTrackVariants(false)} className="accent-[#55ACEE]" />
              <span className="text-sm">Producto simple</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="product_type" checked={trackVariants} onChange={() => setTrackVariants(true)} className="accent-[#55ACEE]" />
              <span className="text-sm">Producto con variantes</span>
            </label>
          </div>
          {trackVariants && (
            <p className="text-xs text-gray-400 mt-2">Las variantes se configuran después de crear el producto.</p>
          )}
        </div>

        {/* ⚙️ Configuración */}
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <p className="text-sm font-medium text-gray-700 mb-3">⚙️ Configuración</p>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">ISV (%)</label>
              <input type="number" step="0.01" min="0" max="100" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#55ACEE]" placeholder="0" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de unidad</label>
              <select value={unitType} onChange={(e) => setUnitType(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#55ACEE]">
                <option value="unit">Unidad</option>
                <option value="weight">Peso</option>
                <option value="meter">Metro</option>
              </select>
            </div>
          </div>
          <div className="mt-4 space-y-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={trackInventory} onChange={(e) => setTrackInventory(e.target.checked)} className="accent-[#55ACEE] w-4 h-4" />
              <span className="text-sm text-gray-700">Rebaja inventario</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={requiresSerial} onChange={(e) => setRequiresSerial(e.target.checked)} className="accent-[#55ACEE] w-4 h-4" />
              <span className="text-sm text-gray-700">Requiere número de serie al vender</span>
            </label>
          </div>
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Tope web inventory</label>
            <input type="number" min="0" value={maxWebInventory} onChange={(e) => setMaxWebInventory(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#55ACEE]" placeholder="Sin límite" />
          </div>
        </div>

        <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#55ACEE]" required />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">SKU</label>
              <input type="text" value={sku} onChange={(e) => setSku(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#55ACEE]" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Categoría</label>
              <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#55ACEE]">
                <option value="">Sin categoría</option>
                {categories.map((cat) => (<option key={cat.id} value={cat.id}>{cat.name}</option>))}
              </select>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Precio {withTax ? '(con ISV)' : '(sin ISV)'} *</label>
              <input type="number" step="0.01" min="0" value={price} onChange={(e) => setPrice(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#55ACEE]" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Costo</label>
              <input type="number" step="0.01" min="0" value={costPrice} onChange={(e) => setCostPrice(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#55ACEE]" />
            </div>
            {!trackVariants && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Stock</label>
                <input type="number" min="0" value={stock} onChange={(e) => setStock(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#55ACEE]" />
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#55ACEE]" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Imágenes</label>
            <input type="file" multiple accept="image/*" onChange={(e) => setImages(e.target.files ? Array.from(e.target.files) : [])} className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#55ACEE]" />
            {images.length > 0 && (<p className="text-sm text-gray-500 mt-1">{images.length} archivo(s) seleccionado(s)</p>)}
          </div>

          <div className="flex justify-end space-x-2">
            <Link href={`/${account.slug}/products`} className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50">Cancelar</Link>
            <button type="submit" disabled={submitting} className="bg-[#FFC107] text-[#202020] px-4 py-2 rounded font-semibold hover:bg-[#e6ad06] transition-colors disabled:opacity-50">{submitting ? 'Guardando...' : 'Crear Producto'}</button>
          </div>
        </form>
      </div>
    </Layout>
  )
}
